import { Box, CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import SearchableSelect from '../SearchableSelect';
import TagInput from '../Test/TagInput';
import Label from '../UI/Label/Label';

const LoadingPlaceholder = ({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      py: '8px',
    }}
  >
    <CircularProgress size={16} sx={{ color: theme === 'dark' ? '#D0D5DD' : '#667085' }} />
    <Typography
      sx={{
        fontSize: '13px',
        color: theme === 'dark' ? '#98A2B3' : '#667085',
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface MicrosoftWordFile {
  id: string;
  name: string;
}

interface PullMicrosoftWordDataProps {
  formData: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
}

const PullMicrosoftWordData: React.FC<PullMicrosoftWordDataProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
  nodeId,
  nodeLabel,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [wordFiles, setWordFiles] = useState<MicrosoftWordFile[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>(
    data?.microsoftWordFileId ?? '',
  );
  const [loadingFiles, setLoadingFiles] = useState(false);

  const loadWordFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await PullDataService.getMicrosoftWordFiles();
      setWordFiles(response.data.files ?? []);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setLoadingFiles(false);
    }
  }, [setNotification]);

  useEffect(() => {
    loadWordFiles();
  }, [loadWordFiles]);

  const fileOptions = wordFiles.map(f => ({
    label: f.name,
    value: f.id,
  }));

  const searchableSelectSx = {
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const selectSx = {
    ...searchableSelectSx,
    width: 200,
    marginTop: '20px',
    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  const contentType = formData.microsoftWordContentType ?? 'fullContent';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '360px',
      }}
    >
      {loadingFiles ? (
        <LoadingPlaceholder theme={user?.theme} label="Loading documents..." />
      ) : (<>
      <Label
        labelStyle={{
          fontSize: '14px',
          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
          fontWeight: 400,
        }}
        labelName="Document"
        isAdvanced
      />

          <SearchableSelect
            options={fileOptions}
            label="Select a Word document"
            onChange={value => {
              const fileId = value ? value.value : '';
              setSelectedFileId(fileId);
              setTimeout(() => {
                onChangeSelect({
                  target: { name: 'microsoftWordFileId', value: fileId },
                });
              }, 0);
            }}
        value={fileOptions.find(o => o.value === selectedFileId) || null}
        className="nodrag"
        style={{ width: 100, marginBottom: '0px' }}
        sx={searchableSelectSx}
        MenuProps={menuPropsSx}
      />

      <Select
        onChange={e =>
          onChangeSelect({
            target: {
              name: 'microsoftWordContentType',
              value: e.target.value,
            },
          })
        }
        value={contentType}
        name="microsoftWordContentType"
        className="nodrag"
        sx={selectSx}
        MenuProps={menuPropsSx}
      >
        <MenuItem
          value="fullContent"
          sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}
        >
          Full Document
        </MenuItem>
        <MenuItem
          value="pullCert"
          sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}
        >
          Pull Certain Paragraphs
        </MenuItem>
      </Select>

      {contentType === 'pullCert' && (
        <>
          <Label
            labelStyle={{
              fontSize: '14px',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              fontWeight: 400,
            }}
            labelName="Paragraphs"
            isAdvanced
          />

          <TagInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (value !== '' && (isNaN(+value) || +value < 0)) {
                setNotification({
                  type: NotificationType.Error,
                  message: 'Paragraph Start must be a positive number',
                });
              }
              onChange({ target: { name: 'paragraphStart', value } });
            }}
            placeholder="Paragraph Start"
            value={formData.paragraphStart ?? ''}
            handleId="paragraphStart"
            dataConnected={data?.paragraphStartData || []}
            isAdvanced
            isCollapsed={false}
            labelName="Paragraph Start"
            advancedCollapsed
            handleSettings={{ top: '22px' }}
            type="number"
            name="paragraphStart"
            nodeId={nodeId}
            nodeLabel={nodeLabel}
          />

          <TagInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (value !== '' && (isNaN(+value) || +value < 0)) {
                setNotification({
                  type: NotificationType.Error,
                  message: 'Paragraph End must be a positive number',
                });
              }
              onChange({ target: { name: 'paragraphEnd', value } });
            }}
            placeholder="Paragraph End"
            value={formData.paragraphEnd ?? ''}
            handleId="paragraphEnd"
            dataConnected={data?.paragraphEndData || []}
            isAdvanced
            isCollapsed={false}
            labelName="Paragraph End"
            advancedCollapsed
            handleSettings={{ top: '22px' }}
            type="number"
            name="paragraphEnd"
            nodeId={nodeId}
            nodeLabel={nodeLabel}
          />
        </>
      )}
      </>)}
    </Box>
  );
};

export default PullMicrosoftWordData;
